import { useApolloClient } from '@apollo/client';
import { SortDirection } from '@busybusy/webapp-react-ui';
import {
  SIMPLE_PROJECT_WITH_METRIC_QUERY,
  SIMPLE_PROJECT_WITH_TIME_ONLY_METRIC_QUERY,
} from 'apollo/queries/project-queries';
import {
  COST_CODES_WITH_METRICS_QUERY,
  COST_CODES_WITH_TIME_METRICS_QUERY,
} from 'containers/activity-reports/queries/cost-code-activity-queries';
import {
  SIMPLE_EQUIPMENT_WITH_METRIC_QUERY,
  SIMPLE_EQUIPMENT_WITH_TIME_METRIC_QUERY,
} from 'containers/activity-reports/queries/equipment-activity-queries';
import {
  MEMBERS_WITH_METRIC_QUERY,
  MEMBERS_WITH_TIME_ONLY_METRIC_QUERY,
} from 'containers/activity-reports/queries/member-activity-queries';
import { useApolloPaging, useTableSorting } from 'hooks';
import useHasCostPermission from 'hooks/permission/useHasCostPermission';
import { isNil } from 'lodash';
import { DateTime } from 'luxon';
import { useState } from 'react';
import { IMember } from 'types';
import IJitLaborMetric from 'types/aggregate/JitLaborMetric';
import ICostCode from 'types/CostCode';
import ICursorable from 'types/Cursorable';
import IEquipment from 'types/Equipment';
import IProject from 'types/Project';
import ITimeRange from 'types/TimeRange';
import { mapNotNull } from 'utils/collectionUtils';
import { remainingDataItemId } from 'utils/constants/utilConstants';
import { dateTimeFromISOWithoutZone } from 'utils/dateUtils';
import {
  getNoCostCodeMetricData,
  getNoEquipmentMetricData,
  getNoProjectMetricData,
  getOrganizationMetrics,
} from 'utils/jitMetricQueries';
import { getMetricLaborTotals } from 'utils/jitMetricUtils';
import { Equipment, LaborMetricsInterval } from '__generated__/graphql';
import { ActivityReportType } from '../../ActivityReportFilter/ActivityReportFilter';
import { IDateActivityTableRowInfo } from './useDateActivity';
import {
  activityReportUtils,
  getActivityReportSortValue,
  shouldIncludeAddOns,
  shouldIncludeMetrics,
} from 'containers/activity-reports/utils/ActivityReportUtils';
import { getActivityReportEquipment } from 'containers/activity-reports/hooks/ActivitySummaryQueryUtils';
import useMultiEquipmentTrackingEnabled from 'hooks/settings/useMultiEquipmentTrackingEnabled/useMultiEquipmentTrackingEnabled';

export default function useDateActivityBasicDetails(
  filterId: string,
  filterType: ActivityReportType,
  timeRange: ITimeRange<DateTime>,
  intervalType: LaborMetricsInterval,
  filterIdWithDescendants?: boolean
) {
  const { getAll } = useApolloPaging();
  const client = useApolloClient();
  const canViewCost = useHasCostPermission();
  const supportsAddOns = useMultiEquipmentTrackingEnabled() && shouldIncludeAddOns(filterType, filterId);
  const [data, setData] = useState<IDateActivityTableRowInfo[]>([]);
  const { sorted, onSort, sortedBy, sortDirection, sortIsDirty } = useTableSorting(
    data,
    'startDate',
    SortDirection.ASCENDING,
    getSortField
  );

  function getSortField(item: IDateActivityTableRowInfo, key: keyof IDateActivityTableRowInfo) {
    if (key === 'startDate') {
      return item.startDate;
    } else {
      return getActivityReportSortValue(item, key, supportsAddOns);
    }
  }

  async function loadData() {
    let metrics: IJitLaborMetric[] | undefined | null;

    if (filterId === remainingDataItemId) {
      metrics = await loadRemainingMetrics();
    } else {
      switch (filterType) {
        case ActivityReportType.BY_COST_CODE:
          metrics = await loadCostCodeMetrics();
          break;
        case ActivityReportType.BY_PROJECT:
          metrics = await loadProjectMetrics();
          break;
        case ActivityReportType.BY_EQUIPMENT:
          metrics = await loadEquipmentMetrics();
          break;
        case ActivityReportType.BY_EMPLOYEE:
          metrics = await loadMemberMetrics();
          break;
        case ActivityReportType.BY_DATE_RANGE:
          metrics = await getOrganizationMetrics(client, timeRange, canViewCost, intervalType);
          break;
        default:
          throw Error('Type of ' + filterType + ' is not supported');
      }
    }

    if (!isNil(metrics)) {
      const equipment = await getActivityReportEquipment(client, filterId, filterType);
      setData(mapNotNull(metrics, (member) => createTableRowInfo(member, equipment)));
    } else {
      setData([]);
    }
  }

  async function loadMemberMetrics() {
    const members = await getAll<IMember & ICursorable>('members', {
      query: canViewCost ? MEMBERS_WITH_METRIC_QUERY : MEMBERS_WITH_TIME_ONLY_METRIC_QUERY,
      variables: {
        first: 1,
        filter: {
          id: { equal: filterId },
        },
        metricsInterval: intervalType,
        metricsStartDate: timeRange.startTime.toISODate(),
        metricsEndDate: timeRange.endTime.toISODate(),
      },
      fetchPolicy: 'network-only',
    });

    return members[0].memberLaborMetrics;
  }

  async function loadProjectMetrics() {
    const projects = await getAll<IProject & ICursorable>('projects', {
      query: canViewCost ? SIMPLE_PROJECT_WITH_METRIC_QUERY : SIMPLE_PROJECT_WITH_TIME_ONLY_METRIC_QUERY,
      variables: {
        first: 1,
        filter: {
          id: { equal: filterId },
        },
        metricsInterval: intervalType,
        metricsStartDate: timeRange.startTime.toISODate(),
        metricsEndDate: timeRange.endTime.toISODate(),
      },
      fetchPolicy: 'network-only',
    });

    return projects[0].projectLaborMetrics;
  }

  async function loadCostCodeMetrics() {
    const costCodes = await getAll<ICostCode & ICursorable>('costCodes', {
      query: canViewCost ? COST_CODES_WITH_METRICS_QUERY : COST_CODES_WITH_TIME_METRICS_QUERY,
      variables: {
        first: 1,
        filter: {
          id: { equal: filterId },
        },
        metricsInterval: intervalType,
        metricsStartDate: timeRange.startTime.toISODate(),
        metricsEndDate: timeRange.endTime.toISODate(),
      },
      fetchPolicy: 'network-only',
    });

    return costCodes[0].costCodeLaborMetrics;
  }

  async function loadEquipmentMetrics() {
    const equipment = await getAll<IEquipment & ICursorable>('equipment', {
      query: canViewCost ? SIMPLE_EQUIPMENT_WITH_METRIC_QUERY : SIMPLE_EQUIPMENT_WITH_TIME_METRIC_QUERY,
      variables: {
        first: 1,
        filter: {
          id: { equal: filterId },
        },
        metricsInterval: intervalType,
        metricsStartDate: timeRange.startTime.toISODate(),
        metricsEndDate: timeRange.endTime.toISODate(),
      },
      fetchPolicy: 'network-only',
    });

    return equipment[0].equipmentLaborMetrics;
  }

  async function loadRemainingMetrics() {
    switch (filterType) {
      case ActivityReportType.BY_PROJECT:
        return getNoProjectMetricData(client, timeRange, canViewCost, intervalType);
      case ActivityReportType.BY_COST_CODE:
        return getNoCostCodeMetricData(client, timeRange, canViewCost, intervalType);
      case ActivityReportType.BY_EQUIPMENT:
        return getNoEquipmentMetricData(client, timeRange, canViewCost, intervalType);
      default:
        throw Error('Type of ' + filterType + ' is not supported for the No Item');
    }
  }

  const createTableRowInfo = (
    metric: IJitLaborMetric,
    equipment: Equipment | null
  ): IDateActivityTableRowInfo | null => {
    const metricTotal = getMetricLaborTotals(metric, filterIdWithDescendants === false);
    if (!shouldIncludeMetrics(metricTotal, supportsAddOns)) {
      return null;
    }

    return {
      id: metric.start,
      startDate: dateTimeFromISOWithoutZone(metric.start),
      endDate: dateTimeFromISOWithoutZone(metric.end),
      ...activityReportUtils.metricToRowInfo(metricTotal, equipment, timeRange),
    };
  };

  return {
    loadData,
    sortedData: sorted,
    onSort,
    sortedBy,
    sortDirection,
    sortIsDirty,
  };
}
